import * as React from 'react';
import { TextSkeleton, type CardProps } from '@chess/api-insights/charts';
import type { MoveKind } from '@chess/api-model';
import { returnTarget } from '@chess/api-navigation';
import { MATERIAL_LABEL, motifLabel, SEVERITY_LABEL, STAGE_LABEL } from '@chess/api-mistakes';
import { KIND_WORDS, pawns as costText } from './cards.ts';
import { scoreLine, scoreTitle } from './cells.ts';
import type { MistakeRow } from './MistakesAggregator.ts';
import { MistakeChips } from './MistakeChips.tsx';
import { tableView, toggleChip, type ChipGroup, type TableOrder, type TableState } from './table.ts';

// Every blunder or miss of the period under the screen's cards: the chips
// that narrow it in place (`MistakeChips`), the Costliest | Newest toggle,
// then a row per move with its classification and its cost in the decisive
// zone: when, against whom, the move, the stage, the pattern (the motif:
// "Pin · against the queen"), what it cost (or would have won), its
// context, the score before → after with the
// swing, the severity, the cost in pawns, and "Practice ›". The first 25
// rows, then "Show all N". A row opens the move's puzzle in the review.
// Every cell keeps to one line: the long texts end in an ellipsis with the
// whole text in their tooltip, and the Score's standings are its tooltip.
// Drawn from props only; the state (`TableState`) is the container's, kept
// in the address.

export type MistakesTableProps = CardProps<
  { mistakes: MistakeRow[] },
  {
    kind: MoveKind;
    /** The chips, the order and "Show all"; the defaults without it. */
    state?: TableState;
    onState?: (state: TableState) => void;
    /** Opens a review address in the app (the row's puzzle); `id` is the row, to return to when the user comes back. */
    onOpen?: (path: string, id: string) => void;
  }
>;

export const DEFAULT_TABLE_STATE: TableState = { filter: {}, order: 'cost', all: false };
const TITLE: Record<MoveKind, string> = { blunder: 'All blunders', miss: 'All misses' };
const MATERIAL_HEAD: Record<MoveKind, string> = { blunder: 'Lost', miss: 'Missed' };
const ORDER_LABEL: Record<TableOrder, string> = { cost: 'Costliest', newest: 'Newest' };
const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const SAMPLE_ROWS = 6;
/** A row's id, unique in the table: the game and the move. */
export const rowId = (m: { gameId: string; ply: number }) => `${m.gameId}:${m.ply}`;

/** A cell's text on one line, cut with an ellipsis at the column's width, whole in its tooltip. */
const Clip: React.FC<{ text: string }> = ({ text }) => (
  <span className="mistakes-clip" title={text}>
    {text}
  </span>
);

const Head: React.FC<{ kind: MoveKind }> = ({ kind }) => (
  <thead>
    <tr>
      <th className="mistakes-number">#</th>
      <th>Date</th>
      <th>Opponent</th>
      <th>Move</th>
      <th>Stage</th>
      <th>Pattern</th>
      <th>{MATERIAL_HEAD[kind]}</th>
      <th>Context</th>
      <th>Score</th>
      <th>Severity</th>
      <th className="mistakes-cost" title="Pawns, counted between −3 and +3">
        Cost
      </th>
      <th aria-label="Practice" />
    </tr>
  </thead>
);

const OrderToggle: React.FC<{ kind: MoveKind; order: TableOrder; onOrder?: (o: TableOrder) => void }> = ({ kind, order, onOrder }) => (
  <div className="seg mistakes-order" role="radiogroup" aria-label="Order">
    {(['cost', 'newest'] as const).map((o) => (
      <label key={o} className="seg-opt">
        <input type="radio" name={`mistakes-order-${kind}`} checked={order === o} onChange={() => onOrder?.(o)} disabled={!onOrder} />
        {ORDER_LABEL[o]}
      </label>
    ))}
  </div>
);

export const MistakesTable: React.FC<MistakesTableProps> = (props) => {
  const { kind, onOpen, onState } = props;
  const state = props.state ?? DEFAULT_TABLE_STATE;
  const { one, many } = KIND_WORDS[kind];
  const set = (next: Partial<TableState>) => onState?.({ ...state, ...next });
  const open = (path: string | undefined, id: string) => (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!path || !onOpen) return;
    e.preventDefault();
    onOpen(path, id);
  };
  const head = (aside: React.ReactNode) => (
    <div className="dash-card-head mistakes-table-head">
      <h3 className="dash-card-title">{TITLE[kind]}</h3>
      <span className="dash-card-aside">{aside}</span>
    </div>
  );

  if (props.loading)
    return (
      <section className="card mistakes-table" aria-label={TITLE[kind]}>
        {head(<TextSkeleton sample="61, costliest first" />)}
        <MistakeChips kind={kind} filter={{}} />
        <div className="mistakes-scroll">
          <table className="table">
            <Head kind={kind} />
            <tbody>
              {Array.from({ length: SAMPLE_ROWS }, (_, i) => (
                <tr key={i}>
                  {['12', 'Oct 1', 'opponent', '23.Qxb7', 'Middlegame', 'Pin · against the queen', 'Knight', 'Sicilian Defense', '+0.4 → −2.8', 'Decisive', '2.8', 'Practice ›'].map((s, j) => (
                    <td key={j}>
                      <TextSkeleton sample={s} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );

  const { mistakes } = props;
  if (!mistakes.length)
    return (
      <section className="card mistakes-table" aria-label={TITLE[kind]}>
        {head(null)}
        <p className="dash-empty">No {many} in the last 30 days.</p>
      </section>
    );

  const view = tableView(mistakes, state);
  const toggle = (group: ChipGroup, value: string) => set({ filter: toggleChip(state.filter, group, value) });
  const noun = (n: number) => (n === 1 ? one : many);
  return (
    <section className="card mistakes-table" aria-label={TITLE[kind]}>
      {head(<OrderToggle kind={kind} order={state.order} onOrder={onState ? (order) => set({ order }) : undefined} />)}
      <MistakeChips kind={kind} filter={state.filter} counts={view.counts} onToggle={onState ? toggle : undefined} onClear={() => set({ filter: {} })} />
      <p className="mistakes-table-note" aria-live="polite">
        {view.filtered ? `${view.matching} of ${view.total} ${noun(view.total)}` : `${view.total} ${noun(view.total)}`}
        {`, ${state.order === 'cost' ? 'costliest' : 'newest'} first`}
      </p>
      {view.matching === 0 ? (
        <p className="dash-empty">No {many} match these chips.</p>
      ) : (
        <div className="mistakes-scroll">
          <table className="table">
            <Head kind={kind} />
            <tbody>
              {view.rows.map((m, i) => {
                const id = rowId(m);
                return (
                  <tr
                    key={id}
                    {...returnTarget(id)}
                    className={`mistakes-row${m.practicePath ? ' mistakes-row-link' : ''}`}
                    title={m.description}
                    tabIndex={m.practicePath ? 0 : undefined}
                    onClick={open(m.practicePath, id)}
                    onKeyDown={(e) => (e.key === 'Enter' ? open(m.practicePath, id)(e) : undefined)}>
                    <td className="mistakes-number">{i + 1}</td>
                    <td>
                      <time dateTime={new Date(m.playedAt).toISOString()}>{day(m.playedAt)}</time>
                    </td>
                    <td className="mistakes-opponent">
                      <Clip text={m.opponent ?? '—'} />
                    </td>
                    <td className="mistakes-move">{m.move}</td>
                    <td>{STAGE_LABEL[m.stage]}</td>
                    <td className="mistakes-pattern">
                      <Clip text={motifLabel(m.motif)} />
                    </td>
                    <td className="mistakes-material">
                      <Clip text={MATERIAL_LABEL[m.kind][m.material]} />
                    </td>
                    <td className="mistakes-context">
                      <Clip text={m.context} />
                    </td>
                    <td className="mistakes-score" title={scoreTitle(m)} aria-label={scoreTitle(m)}>
                      {scoreLine(m)}
                    </td>
                    <td>
                      <span className={`mistakes-severity mistakes-severity-${m.severity}`}>{SEVERITY_LABEL[m.severity]}</span>
                    </td>
                    <td className="mistakes-cost">{costText(m.cost)}</td>
                    <td className="mistakes-practice">
                      {m.practicePath ? (
                        <a
                          href={m.practicePath}
                          onClick={(e) => {
                            e.stopPropagation();
                            open(m.practicePath, id)(e);
                          }}>
                          Practice ›
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!state.all && view.matching > view.rows.length && (
        <button type="button" className="mistakes-show-all" onClick={() => set({ all: true })} disabled={!onState}>
          Show all {view.matching}
        </button>
      )}
    </section>
  );
};
